import { forwardRef, useImperativeHandle, useState } from 'react'
import type { PackagingComponentInput } from '@/shared/hooks/api/packagingSuppliers'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { ComponentFormFields } from './ComponentFormFields'
import { ComponentsTable } from './ComponentsTable'
import type {
  ComponentDraft,
  ComponentEditorLabels,
} from './componentEditorTypes'
import {
  createEmptyPackagingComponent,
  isComponentValid,
  normalizeComponent,
} from './componentEditorUtils'
import { formatComponentRecyclingPctForFormInput } from './componentRecyclingPct'
import { formatComponentWeightForFormInput } from './componentWeightGrams'

export type { ComponentEditorLabels } from './componentEditorTypes'

type ComponentEditorProps = {
  components: PackagingComponentInput[]
  onChange: (components: PackagingComponentInput[]) => void
  weightPercentages?: number[]
  isSaving?: boolean
  showAddButton?: boolean
  showEmptyMessage?: boolean
  readOnly?: boolean
  labels: ComponentEditorLabels
}

export type ComponentEditorHandle = {
  openAddSheet: () => void
}

export const ComponentEditor = forwardRef<
  ComponentEditorHandle,
  ComponentEditorProps
>(function ComponentEditor(
  {
    components,
    onChange,
    weightPercentages,
    isSaving = false,
    showAddButton = true,
    showEmptyMessage = true,
    readOnly = false,
    labels,
  },
  ref
) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [draft, setDraft] = useState<ComponentDraft>(
    createEmptyPackagingComponent()
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const closeSheet = () => {
    setSheetOpen(false)
    setEditingIndex(null)
    setShowValidationErrors(false)
  }

  const openAddSheet = () => {
    setDraft(createEmptyPackagingComponent())
    setEditingIndex(null)
    setShowValidationErrors(false)
    setSheetOpen(true)
  }

  useImperativeHandle(ref, () => ({
    openAddSheet,
  }))

  const openEditSheet = (index: number) => {
    const component = components[index]
    setDraft({
      name: component.name,
      material: component.material,
      weightInput: formatComponentWeightForFormInput(component.weightInGrams),
      recyclable: component.recyclable,
      recyclingStream: component.recyclingStream,
      recyclingPctInput: formatComponentRecyclingPctForFormInput(
        component.recyclingPct
      ),
      reusable: component.reusable,
      notes: component.notes,
    })
    setEditingIndex(index)
    setShowValidationErrors(false)
    setSheetOpen(true)
  }

  const removeComponent = (index: number) => {
    onChange(components.filter((_, componentIndex) => componentIndex !== index))
  }

  const confirmSheet = () => {
    setShowValidationErrors(true)
    if (!isComponentValid(draft)) {
      return
    }
    const normalized = normalizeComponent(draft)
    if (editingIndex === null) {
      onChange([...components, normalized])
    } else {
      onChange(
        components.map((component, componentIndex) => {
          if (componentIndex !== editingIndex) {
            return component
          }
          return normalized
        })
      )
    }
    closeSheet()
  }

  const hasComponents = components.length > 0
  const showEmptyState = !hasComponents && showEmptyMessage
  const showComponentsTable = hasComponents
  const isAddingComponent = editingIndex === null

  let sheetTitle = labels.editComponent
  if (isAddingComponent) {
    sheetTitle = labels.addComponent
  }

  let sheetIdPrefix = `component-${editingIndex}`
  if (isAddingComponent) {
    sheetIdPrefix = 'new-component'
  }

  let confirmButtonLabel = labels.saveComponent
  if (isAddingComponent) {
    confirmButtonLabel = labels.addComponent
  }

  return (
    <div className='space-y-4'>
      {showEmptyState && (
        <p className='text-sm text-muted-foreground'>{labels.emptyMessage}</p>
      )}

      {showComponentsTable && (
        <ComponentsTable
          components={components}
          weightPercentages={weightPercentages}
          isSaving={isSaving}
          labels={labels}
          onEdit={openEditSheet}
          onRemove={removeComponent}
          showActions={!readOnly}
        />
      )}

      {showAddButton && !readOnly && (
        <Button
          type='button'
          variant='outline'
          onClick={openAddSheet}
          disabled={isSaving}
        >
          {labels.addComponent}
        </Button>
      )}

      {isSaving && (
        <p className='text-sm text-muted-foreground'>{labels.saving}</p>
      )}

      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeSheet()
          }
        }}
      >
        <SheetContent
          side='right'
          className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md'
        >
          <div className='flex min-h-0 flex-1 flex-col'>
            <SheetHeader className='shrink-0 border-b px-6 py-4 text-start'>
              <SheetTitle>{sheetTitle}</SheetTitle>
            </SheetHeader>
            <div className='flex-1 overflow-y-auto px-6 py-4'>
              <ComponentFormFields
                component={draft}
                onChange={setDraft}
                idPrefix={sheetIdPrefix}
                labels={labels}
                showValidationErrors={showValidationErrors}
              />
            </div>
            <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
              <Button type='button' variant='outline' onClick={closeSheet}>
                {labels.cancel}
              </Button>
              <Button type='button' onClick={confirmSheet}>
                {confirmButtonLabel}
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
})
